import React, {useEffect, useRef, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Avatar} from "primereact/avatar";
import {Button} from "primereact/button";
import {ColorPicker} from "primereact/colorpicker";
import {ConfirmDialog, confirmDialog} from "primereact/confirmdialog";
import {Dialog} from "primereact/dialog";
import {Divider} from "primereact/divider";
import {Dropdown} from "primereact/dropdown";
import {InputSwitch} from "primereact/inputswitch";
import {InputText} from "primereact/inputtext";
import {Message} from "primereact/message";
import {OverlayPanel} from "primereact/overlaypanel";
import {ProgressBar} from "primereact/progressbar";
import {RadioButton} from "primereact/radiobutton";
import {Slider} from "primereact/slider";
import {TabPanel, TabView} from "primereact/tabview";
import {setMessage} from "../../../system/app-slice";
import Icon from "../../../system/components/icon";
import SelectMember from "../../tasks/components/select-member";
import SelectIcon from "../../tasks/components/select-icon";
import {useTranslate} from "../../../system/useTranslate";
import {useTeam} from "../../../models/team/use-team";
import {teamDisplayName} from "../team-display-name";
import {RichTextEditor} from "../../../system/components/rich-text";

const DEFAULT_ACCESS = {version: 1, visibility: "team-members-own-projects", invite: "just-admin"};
const DEFAULT_HEALTH = {version: 1, metrics: [
    {key: "on-time", enabled: true, weight: 100, dataSource: "done-tasks"},
    {key: "throughput", enabled: true, weight: 100, dataSource: "done-tasks"},
    {key: "quality", enabled: true, weight: 100, dataSource: "task-status"},
]};

function normalizeHealth(value) {
    const incoming = Array.isArray(value) ? value : value?.metrics;
    const byKey = new Map((incoming || []).map((item) => [item?.key || item?.id, item]));
    return {version: 1, metrics: DEFAULT_HEALTH.metrics.map((fallback) => ({...fallback, ...(byKey.get(fallback.key) || {})}))};
}

function initialDraft(team, translate) {
    return {
        name: teamDisplayName(team, translate),
        description: team?.description || "",
        owner: team?.owner || null,
        iconColor: team?.iconColor || {icon: "mi mi:groups", color: "orange"},
        teamRules: Array.isArray(team?.teamRules) ? team.teamRules : [],
        notifications: team?.notifications !== false,
        accessConfigs: {...DEFAULT_ACCESS, ...(team?.accessConfigs || {})},
        teamHealthIndex: normalizeHealth(team?.teamHealthIndex),
    };
}

function TeamSettingsDialog({visible, setVisible, teamData, onSubmit, onArchived}) {
    const translate = useTranslate();
    const dispatch = useDispatch();
    const teamApi = useTeam();
    const memberships = useSelector((state) => state.app.workspaceMembershipData?.workspace?.workspace_memberships || []);
    const [draft, setDraft] = useState(() => initialDraft(teamData, translate));
    const [activeIndex, setActiveIndex] = useState(0);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [health, setHealth] = useState(null);
    const [healthLoading, setHealthLoading] = useState(false);

    useEffect(() => {
        if (!visible) return;
        setDraft(initialDraft(teamData, translate));
        setActiveIndex(0);
        setError(null);
        setHealth(null);
    }, [teamData, translate, visible]);

    const loadHealth = async () => {
        setHealthLoading(true);
        try { setHealth(await teamApi.getTeamHealth(teamData.documentId)); }
        catch (requestError) { setError(requestError?.message || "team.health.load-error"); }
        finally { setHealthLoading(false); }
    };

    useEffect(() => {
        if (visible && activeIndex === 1 && !health) loadHealth();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeIndex, visible]);

    const save = async () => {
        const normalizedName = draft.name.trim();
        if (!normalizedName) { setError("team.error.name-required"); return; }
        setSaving(true);
        setError(null);
        try {
            const updated = await teamApi.updateTeam(teamData.documentId, {
                ...draft,
                name: normalizedName === teamDisplayName(teamData, translate) ? teamData.name : normalizedName,
                owner: draft.owner ? (draft.owner.documentId || draft.owner.id) : null,
            });
            dispatch(setMessage({severity: "success", content: translate("team.settings.saved")}));
            await onSubmit?.({value: updated});
            setVisible(false);
        } catch (requestError) {
            setError(requestError?.message || "team.settings.save-error");
        } finally { setSaving(false); }
    };

    const archive = () => confirmDialog({
        header: translate("archive-team-title"),
        message: translate("team.archive.confirm").replace("{{TEAM}}", teamDisplayName(teamData, translate)),
        icon: "pi pi-exclamation-triangle",
        acceptLabel: translate("archive-team"),
        rejectLabel: translate("cancel"),
        acceptClassName: "p-button-danger text-xs",
        rejectClassName: "p-button-text text-xs",
        accept: async () => {
            setSaving(true);
            try {
                await teamApi.archiveTeam(teamData.documentId);
                dispatch(setMessage({severity: "success", content: translate("team.archive.success")}));
                setVisible(false);
                onArchived?.();
            } catch (requestError) {
                setError(requestError?.message || "team.archive.error");
            } finally { setSaving(false); }
        },
    });

    const footer = <div className="flex flex-row-reverse gap-2 p-2">
        <Button className="text-xs gap-2" icon="pi pi-save" loading={saving} label={translate("save-changes")} onClick={save}/>
        <Button outlined className="text-xs" disabled={saving} label={translate("cancel")} onClick={() => setVisible(false)}/>
    </div>;

    return <>
        <ConfirmDialog className="rtl"/>
        <Dialog visible={Boolean(visible)} modal className="rtl w-11 lg:w-7" contentClassName="p-0" footer={footer}
            header={<div><strong className="block text-lg">{translate("team-settings")}</strong><small className="text-500">{translate("team-settings-description").replace("{{TEAM_NAME}}", teamDisplayName(teamData, translate))}</small></div>}
            onHide={() => !saving && setVisible(false)}>
            <TabView activeIndex={activeIndex} onTabChange={(event) => setActiveIndex(event.index)} panelContainerClassName="p-0">
                <TabPanel header={translate("team-global-settings")} leftIcon={<Icon icon="mi mi:settings" className="pl-2"/>}>
                    <GeneralSettings draft={draft} setDraft={setDraft} memberships={memberships} onArchive={archive} translate={translate}/>
                </TabPanel>
                <TabPanel header={translate("team-health-settings")} leftIcon={<Icon icon="mi mi:ecg_heart" className="pl-2"/>}>
                    <HealthSettings draft={draft} setDraft={setDraft} health={health} loading={healthLoading} onRefresh={loadHealth} translate={translate}/>
                </TabPanel>
            </TabView>
            {error && <Message severity="error" className="m-3" text={translate(error)}/>}
        </Dialog>
    </>;
}

function Section({icon, title, children}) {
    return <section className="flex flex-column gap-3"><div className="flex align-items-center gap-2 border-bottom-1 border-100 pb-2"><Icon icon={icon} className="text-primary text-xl"/><strong>{title}</strong></div>{children}</section>;
}

function GeneralSettings({draft, setDraft, memberships, onArchive, translate}) {
    const access = draft.accessConfigs;
    return <div className="p-4 flex flex-column gap-5 max-h-60vh overflow-auto">
        <Section icon="mi mi:dashboard" title={translate("main-info")}>
            <div className="grid">
                <div className="col-12 md:col-6 flex flex-column gap-2"><label htmlFor="team-settings-name">{translate("team-name")}</label><InputText id="team-settings-name" value={draft.name} onChange={(event) => setDraft((current) => ({...current, name: event.target.value}))}/></div>
                <div className="col-12 md:col-6 flex flex-column gap-2"><label>{translate("team-owner")}</label><SelectMember primeDropdown ariaLabel={translate("team-owner")} selectedMember={draft.owner} onChange={(owner) => setDraft((current) => ({...current, owner}))} members={memberships}/></div>
                <div className="col-12 flex flex-column gap-2"><label htmlFor="team-settings-description">{translate("team-description")}</label><RichTextEditor ariaLabel={translate("team-description")} value={draft.description} onChange={(html) => setDraft((current) => ({...current, description: html}))} minHeight="5rem"/></div>
                <div className="col-12 flex flex-column gap-2"><label>{translate("team-color")}</label><IconColorPicker value={draft.iconColor} onChange={(iconColor) => setDraft((current) => ({...current, iconColor}))}/></div>
            </div>
        </Section>
        <Section icon="mi mi:edit_document" title={translate("team-rules")}>
            <RuleEditor value={draft.teamRules} onChange={(teamRules) => setDraft((current) => ({...current, teamRules}))} translate={translate}/>
        </Section>
        <Section icon="mi mi:notifications_active" title={translate("notification")}>
            <div className="surface-50 border-1 border-100 border-round-xl p-3 flex align-items-center justify-content-between gap-3"><div><strong className="block mb-1">{translate("update-team-activity-title")}</strong><small className="text-500">{translate("update-team-activity-description")}</small></div><InputSwitch checked={draft.notifications} onChange={(event) => setDraft((current) => ({...current, notifications: event.value}))}/></div>
        </Section>
        <Section icon="mi mi:lock_person" title={translate("access-settings")}>
            <AccessCard title="team-member-visibility-title" name="team-visibility" value={access.visibility}
                options={["team-members-own-projects", "team-members-others-projects", "team-members-all-projects"]}
                onChange={(visibility) => setDraft((current) => ({...current, accessConfigs: {...current.accessConfigs, version: 1, visibility}}))} translate={translate}/>
            <AccessCard title="who-can-invite-title" name="team-invite" value={access.invite} options={["just-admin", "all-members"]}
                onChange={(invite) => setDraft((current) => ({...current, accessConfigs: {...current.accessConfigs, version: 1, invite}}))} translate={translate}/>
        </Section>
        <Section icon="mi mi:archive" title={translate("archive-team-title")}>
            <div className="border-1 border-red-200 bg-red-50 border-round-xl p-3 flex flex-column gap-3"><p className="m-0 text-xs text-red-600 line-height-3">{translate("archive-team-description")}</p><Button severity="danger" className="text-xs gap-2" icon="pi pi-box" label={translate("archive-team")} onClick={onArchive}/></div>
        </Section>
    </div>;
}

function AccessCard({title, name, value, options, onChange, translate}) {
    return <div className="surface-50 border-1 border-100 border-round-xl p-3"><strong className="block mb-3">{translate(title)}</strong><div className="flex flex-column gap-2">{options.map((option) => <label key={option} className="flex align-items-center gap-2 cursor-pointer"><RadioButton name={name} value={option} checked={value === option} onChange={() => onChange(option)}/><span>{translate(option)}</span></label>)}</div></div>;
}

function RuleEditor({value, onChange, translate}) {
    const add = () => onChange([...(value || []), {id: `rule-${Date.now()}`, title: "", items: [""]}]);
    const update = (index, next) => onChange(value.map((rule, itemIndex) => itemIndex === index ? next : rule));
    const remove = (index) => onChange(value.filter((_, itemIndex) => itemIndex !== index));
    const updateItem = (ruleIndex, itemIndex, itemValue) => {
        const rule = value[ruleIndex];
        const items = [...(rule.items || [])];
        items[itemIndex] = itemValue;
        update(ruleIndex, {...rule, items});
    };
    const addItem = (ruleIndex) => {
        const rule = value[ruleIndex];
        update(ruleIndex, {...rule, items: [...(rule.items || []), ""]});
    };
    const removeItem = (ruleIndex, itemIndex) => {
        const rule = value[ruleIndex];
        update(ruleIndex, {...rule, items: (rule.items || []).filter((_, index) => index !== itemIndex)});
    };
    return <div className="flex flex-column gap-3">{(value || []).map((rule, ruleIndex) => <div key={rule.id || ruleIndex} className="surface-50 border-1 border-100 border-round-xl p-3 flex flex-column gap-2">
        <div className="flex gap-2"><InputText className="flex-1" value={rule.title || ""} placeholder={translate("team.rules.title-placeholder")} onChange={(event) => update(ruleIndex, {...rule, title: event.target.value})}/><Button text rounded severity="danger" icon="pi pi-trash" aria-label={translate("delete")} onClick={() => remove(ruleIndex)}/></div>
        {(rule.items || []).map((item, itemIndex) => <div key={`${rule.id || ruleIndex}-item-${itemIndex}`} className="flex gap-2 pr-3"><InputText className="flex-1" value={typeof item === "string" ? item : item?.text || ""} placeholder={translate("team.rules.item-placeholder")} onChange={(event) => updateItem(ruleIndex, itemIndex, event.target.value)}/><Button text rounded severity="danger" icon="pi pi-times" aria-label={translate("team.rules.remove-item")} onClick={() => removeItem(ruleIndex, itemIndex)}/></div>)}
        <Button text className="text-xs align-self-start" icon="pi pi-plus" label={translate("team.rules.add-item")} onClick={() => addItem(ruleIndex)}/>
    </div>)}<Button outlined className="text-xs gap-2" icon="pi pi-plus" label={translate("create-new-rule")} onClick={add}/></div>;
}

function HealthSettings({draft, setDraft, health, loading, onRefresh, translate}) {
    const updateMetric = (key, patch) => setDraft((current) => ({...current, teamHealthIndex: {...current.teamHealthIndex, version: 1, metrics: current.teamHealthIndex.metrics.map((item) => item.key === key ? {...item, ...patch} : item)}}));
    const healthByKey = new Map((health?.metrics || []).map((item) => [item.key, item]));
    const sources = ["done-tasks", "task-status"];
    return <div className="p-4 flex flex-column gap-4 max-h-60vh overflow-auto">
        <div className="surface-50 border-1 border-100 border-round-xl p-4 flex align-items-center gap-3"><div className="flex-1"><strong className="block mb-2">{translate("team.health.score")}</strong>{loading ? <ProgressBar mode="indeterminate" className="h-1rem"/> : health?.score === null || health?.score === undefined ? <small className="text-500">{translate("team.health.insufficient")}</small> : <ProgressBar value={health.score}/>}</div><Button text rounded icon="pi pi-refresh" loading={loading} aria-label={translate("team.health.refresh")} onClick={onRefresh}/></div>
        {draft.teamHealthIndex.metrics.map((metric) => <article key={metric.key} className="surface-50 border-1 border-100 border-round-xl p-3 grid m-0 align-items-center">
            <div className="col-12 lg:col-4 flex align-items-center justify-content-between gap-3 min-w-0">
                <div className="flex-1 min-w-0"><strong className="block text-sm">{translate(`team.health.metric.${metric.key}`)}</strong><small className="text-500 line-height-3">{translate(`team.health.metric.${metric.key}.description`)}</small></div>
                <span className="inline-flex align-items-center justify-content-center flex-shrink-0" style={{width: "3rem", minWidth: "3rem"}}>
                    <InputSwitch className="flex-shrink-0" checked={metric.enabled} onChange={(event) => updateMetric(metric.key, {enabled: event.value})}/>
                </span>
            </div>
            <div className="col-12 lg:col-4 flex align-items-center gap-3"><Slider disabled={!metric.enabled} className="flex-1" value={metric.weight} onChange={(event) => updateMetric(metric.key, {weight: event.value})}/><span>{Number(metric.weight).toLocaleString("fa-IR")}%</span></div>
            <div className="col-12 lg:col-3"><Dropdown disabled={!metric.enabled} panelClassName="rtl" className="w-full" value={metric.dataSource} options={sources.map((source) => ({label: translate(source), value: source}))} onChange={(event) => updateMetric(metric.key, {dataSource: event.value})}/></div>
            <div className="col-12 lg:col-1 text-left"><strong>{healthByKey.get(metric.key)?.value === null || healthByKey.get(metric.key)?.value === undefined ? "—" : `${Number(healthByKey.get(metric.key).value).toLocaleString("fa-IR")}%`}</strong></div>
        </article>)}
        {health?.calculatedAt && <small className="text-500">{translate("team.health.sample-size").replace("{{COUNT}}", Number(health.sampleSize || 0).toLocaleString("fa-IR"))}</small>}
    </div>;
}

function IconColorPicker({value, onChange}) {
    const icons = useRef();
    const colors = useRef();
    const palette = ["red", "blue", "purple", "green", "orange"];
    return <div className="flex align-items-center gap-2">
        <Avatar shape="circle" size="large" className="border-2 border-dashed border-300 cursor-pointer" icon={<Icon icon={value?.icon || "mi mi:image"}/>} onClick={(event) => icons.current.toggle(event)}/>
        <OverlayPanel ref={icons} pt={{root: () => ({className: "rtl"})}}><SelectIcon onChange={(icon) => {onChange({...value, icon}); icons.current.hide();}}/></OverlayPanel>
        <Divider layout="vertical" className="m-1"/>
        {palette.map((color) => <Avatar key={color} shape="circle" className="cursor-pointer border-2 border-white shadow-1" style={{backgroundColor: color}} icon={color === value?.color ? "pi pi-check" : undefined} onClick={() => onChange({...value, color})}/>)}
        <Avatar shape="circle" className="cursor-pointer border-2 border-dashed border-300" style={{backgroundColor: value?.color}} icon="pi pi-plus" onClick={(event) => colors.current.toggle(event)}/>
        <OverlayPanel ref={colors}><ColorPicker inline value={String(value?.color || "").replace("#", "")} onChange={(event) => onChange({...value, color: `#${event.value}`})}/></OverlayPanel>
    </div>;
}

export default TeamSettingsDialog;
